import { type ComponentType } from 'react';

import { type ClientLoader, type JavaScriptModule } from './loader';

export type CreateDeferredOptions<C> = {
	loader: ClientLoader<C>;
	preload: ClientLoader<C>;
};

export type Deferred<C> = {
	preload(): void;
	promise: Promise<JavaScriptModule<C>>;
	result: JavaScriptModule<C> | void;
	start(): Promise<void>;
};

/**
 * a map of all created deferred to
 * - ensure there is single deferred created for every lazy (NOT YET ENFORCED/USED)
 * - track list of known LazyComponents so we can, for example, preload them by id
 */
const lazyIds = new Map<string, Deferred<any>>();

/**
 * ids "marked" for preload on spot
 */
let preloadIds: string[];

export const preloadMarkedIds = (preload: string[]): void => {
	preloadIds = preload;

	try {
		preloadIds.forEach((lazyId) => {
			const deffered = lazyIds.get(lazyId);
			if (deffered) {
				// preload dont have any "phases" and visa versa. Just load it ASAP
				deffered.preload();
			}
		});
	} catch (e) {
		// eslint-disable-next-line no-console
		console.error('RLL preload', e);
	}
};

export const createDeferred = <C extends ComponentType<any>>(
	lazyId: string,
	{ loader, preload }: CreateDeferredOptions<C>,
): Deferred<C> => {
	// we dont cache deferred FOR NOW as the goal is to handle networking first
	// the next step would in better pre-resolving deferred, so Suspense might be never triggered

	// if (lazyIds.has(lazyId)) {
	// 	return lazyIds.get(lazyId)!;
	// }

	let resolve: (m: any) => void;

	const deferred = {
		promise: new Promise<JavaScriptModule<C>>((res) => {
			resolve = (m: any) => {
				let withDefault;
				deferred.result = m;

				if (!m.default) {
					withDefault = { default: m };
				}

				res(withDefault ? withDefault : m);
			};
		}),
		result: undefined,
		preload: () => {
			if (deferred.result) {
				return;
			}

			preload()
				.then((m: any) => {
					deferred.result = m;
				})
				.catch(() => {
					// Do nothing...
				});
		},
		start: () => {
			if (deferred.result) {
				resolve(deferred.result);

				return Promise.resolve();
			}

			// Make a new loader request when none have started or resolved yet
			return loader().then(resolve);
		},
	};

	lazyIds.set(lazyId, deferred);

	// in case of "just created" deferred is already marked for preload
	// do instant preload
	if (preloadIds?.includes(lazyId)) {
		deferred.preload();
	}

	return deferred;
};
